import { Fragment, MouseEvent, useEffect, useId, useRef, useState } from "react";
import { styled } from "@mui/material";
import { STATIC_MAP_ATTRIBUTION } from "../../utils/staticMap";

/**
 * The static map's required credits — Mapbox Product Terms §1.4: the Mapbox logo, plus the
 * STATIC_MAP_ATTRIBUTION links with "Improve this map". The image's own logo and text attribution
 * are off (see utils/staticMap), so both are drawn here, inside the panel and clear of its rounded
 * corners, where the image's would be clipped.
 *
 * Both are mapbox-gl's own controls, as markup only (there's no GL map here to add them to): the
 * global mapbox-gl.css (src/index.tsx) draws them, so they look exactly as on the interactive maps.
 * (public/index.html only hides `.mapboxgl-control-container`, which neither is in.)
 *
 * Logo: LogoControl's `a.mapboxgl-ctrl-logo` — the official wordmark, unstyled, as the terms
 * require. Mapbox lets it move from its default lower-left corner as long as it stays visible on
 * the map, so it sits at the lower right beside the attribution.
 *
 * Attribution: AttributionControl with `compact: true` — the "i" button, which toggles
 * `mapboxgl-compact-show` to open the links in a white pill growing leftward (the logo moves
 * with it). It shows and hides instantly, as Mapbox's does; nothing here animates.
 */
const MapAttribution = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  // Close on Escape or a press anywhere else, like the other popovers.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  // Clicks in here are for the credits, not the panel's land link.
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <Root ref={rootRef} onClick={stop}>
      <Logo
        className="mapboxgl-ctrl-logo"
        href="https://www.mapbox.com/"
        target="_blank"
        rel="noopener nofollow"
        aria-label="Mapbox home"
      />
      <Attrib className={`mapboxgl-ctrl mapboxgl-ctrl-attrib mapboxgl-compact${open ? " mapboxgl-compact-show" : ""}`}>
        <button
          type="button"
          className="mapboxgl-ctrl-attrib-button"
          aria-label="Toggle attribution"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="mapboxgl-ctrl-icon" aria-hidden="true" title="Toggle attribution" />
        </button>
        <div id={listId} className="mapboxgl-ctrl-attrib-inner">
          {STATIC_MAP_ATTRIBUTION.map((a, i) => (
            <Fragment key={a.href}>
              {i > 0 && " "}
              <a
                href={a.href}
                target="_blank"
                rel="noopener noreferrer"
                className={a.improve ? "mapbox-improve-map" : undefined}
              >
                {a.label}
              </a>
            </Fragment>
          ))}
        </div>
      </Attrib>
    </Root>
  );
};

// Spans the panel's bottom edge, clear of its 20px corner radius, so the open links can wrap in
// the width left of the logo on a narrow panel. Click-through itself: the logo and the control
// (`.mapboxgl-ctrl` turns pointer events back on) take the pointer, the rest stays the land link's.
// The font is `.mapboxgl-map`'s, which the control would inherit inside a GL map.
const Root = styled("div")({
  position: "absolute",
  left: 12,
  right: 12,
  bottom: 10,
  zIndex: 3,
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-end",
  gap: 6,
  pointerEvents: "none",
  font: "12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif",
});

// `&&` outranks mapbox-gl.css's `a.mapboxgl-ctrl-logo` margin (a nudge for its own control
// layout); the logo itself is left exactly as Mapbox draws it.
const Logo = styled("a")({
  flexShrink: 0,
  pointerEvents: "auto",
  "&&": { margin: 0 },
});

// Only the compact control's 10px corner margin goes (Root does the inset); the rest is Mapbox's.
const Attrib = styled("div")({
  minWidth: 0,
  "&&&": { margin: 0 },
});

export default MapAttribution;
